import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { SiteContext } from '@icetus/contracts';
import { Badge, Button } from '@icetus/ui';
import { cachedPlate, resolveLogoPlate } from '../lib/logo-plate';
import { planLabel } from '../lib/plan-label';
import { siteOrigin } from '../lib/site-url';
import { LocaleFlag } from './locale-flag';

/**
 * 站点卡片（竖版）。
 *
 * 一个租户最多 3 个站点（`business` 档），所以这里不是"列很多行"的表格场景，
 * 而是"最多三张、每张都该有脸"的展示场景。原来用的是通用 `Card`：一行标题、
 * 两列 kv、一排按钮，横着一拉满屏，三个站点长得一模一样 —— 客户扫一眼分不清
 * 哪张是哪个站。
 *
 * 于是改成竖版海报式：
 *   · 顶部封面区：Logo（没配就显示站点名首字）+ 序号，靠"图"把几张区分开；
 *   · 正文每行都带图标：域名、语言（国旗）、状态（状态点）、主题；
 *   · 底部两个动作固定在卡片底边，三张卡片并排时按钮在同一水平线上。
 *
 * 封面刻意用**固定的深色渐变**而不是站点品牌色兜底：品牌色可能是白的，
 * 白底白字等于没字。品牌色只作为左上角那团柔光参与，深浅都不影响可读性。
 */
export function SiteCard({
  context,
  index,
  busy,
  resumeBlocked,
  hint,
  onResume,
  onArchive,
  onManageDomains,
}: {
  context: SiteContext;
  /** 从 1 开始的序号，显示成「站点 1」 */
  index: number;
  busy: boolean;
  resumeBlocked: boolean;
  /**
   * 暂停原因与恢复条件。由调用方拼好传进来 —— 里面要写「当前套餐只允许
   * N 个站点同时在线（已有 M 个）」，而 N/M 来自站点列表的用量统计，
   * 卡片本身不该反过来去查这些。
   */
  hint?: ReactNode;
  onResume: () => void;
  /**
   * 归档。不给就不显示按钮（例如平台运维视图里不该出现客户的动作）。
   *
   * 归档是降档流程的出口：被暂停的站仍占名额，客户不归档就既恢复不了、
   * 也建不了新站。它不删数据，所以确认框里写的是"随时能开回来"。
   */
  onArchive?: () => void;
  onManageDomains: () => void;
}) {
  const { site, tenant, locales, branding, readOnly } = context;
  const suspended = site.status === 'suspended';
  const archived = site.status === 'archived';
  const initial = site.name.trim().slice(0, 1).toUpperCase() || '·';

  /**
   * Logo 地址可能是相对的（示例主题上传的是 `/brand/logo.png`）。
   *
   * 这个字段原本是给**站点**渲染用的，相对地址在站点域名下成立；控制台是
   * 另一个域名，直接拿来当 `src` 会去 console 根下找一张不存在的图 ——
   * 卡片上就是一块空白封面，客户以为 Logo 丢了。所以按站点域名补全。
   */
  const logoUrl = useMemo(() => {
    const raw = branding.logoUrl?.trim();
    if (!raw) return null;
    if (/^(https?:)?\/\//i.test(raw) || raw.startsWith('data:')) return raw;
    if (!site.primaryHost) return null;
    return `${siteOrigin(site.primaryHost)}${raw.startsWith('/') ? '' : '/'}${raw}`;
  }, [branding.logoUrl, site.primaryHost]);

  /**
   * 底板：深色 Logo 垫白板，浅色 Logo 直接压在封面上（详见 `lib/logo-plate`）。
   * 没量出来之前先按白板画 —— 深色 Logo 占绝大多数，猜错的代价最小。
   */
  const [plate, setPlate] = useState<'none' | 'white' | null>(() => cachedPlate(logoUrl));
  useEffect(() => {
    if (!logoUrl) return;
    const known = cachedPlate(logoUrl);
    if (known) {
      setPlate(known);
      return;
    }
    let cancelled = false;
    void resolveLogoPlate(logoUrl).then((next) => {
      if (!cancelled) setPlate(next);
    });
    return () => {
      cancelled = true;
    };
  }, [logoUrl]);

  /* 品牌色只做成封面左上角的柔光。亮色品牌色混成半透明后仍然只提亮、
     不改变底色，所以不用管它是深是浅 */
  const coverStyle = branding.brandColor
    ? ({
        '--cover-glow': `color-mix(in srgb, ${branding.brandColor} 55%, transparent)`,
      } as CSSProperties)
    : undefined;

  return (
    <article className={`sitecard${suspended ? ' is-suspended' : ''}${archived ? ' is-archived' : ''}`}>
      <div className="sitecard__cover" style={coverStyle}>
        <span className="sitecard__no">站点 {index}</span>
        {logoUrl ? (
          <img
            className={`sitecard__logo${plate === 'none' ? ' sitecard__logo--bare' : ''}`}
            src={logoUrl}
            alt=""
            loading="lazy"
          />
        ) : (
          <span className="sitecard__initial" aria-hidden="true">
            {initial}
          </span>
        )}
      </div>

      <div className="sitecard__body">
        <div className="sitecard__head">
          <h3 className="sitecard__name" title={site.name}>
            {site.name}
          </h3>
          <div className="sitecard__badges">
            {archived ? <Badge tone="neutral">已归档</Badge> : suspended ? <Badge tone="danger">已暂停</Badge> : null}
            <Badge tone="neutral">{planLabel(tenant.planCode)}</Badge>
          </div>
        </div>

        {site.primaryHost ? (
          <a
            className="sitecard__host"
            href={`https://${site.primaryHost}`}
            target="_blank"
            rel="noreferrer"
          >
            <IconGlobe />
            <span>{site.primaryHost}</span>
          </a>
        ) : (
          <p className="sitecard__host is-empty">
            <IconGlobe />
            <span>未绑定域名</span>
          </p>
        )}

        <dl className="sitecard__facts">
          <div>
            <dt>
              <IconChat />
              语言
            </dt>
            <dd className="sitecard__locales">
              {locales.map((locale) => (
                <span key={locale} className="locale-chip">
                  <LocaleFlag locale={locale} />
                  {locale.toUpperCase()}
                </span>
              ))}
            </dd>
          </div>
          <div>
            <dt>
              <IconPulse />
              状态
            </dt>
            <dd className="sitecard__status">
              <span
                className={
                  archived || suspended || readOnly ? 'sitecard__dot is-warn' : 'sitecard__dot'
                }
              />
              {archived
                ? '已归档（不占名额）'
                : suspended
                  ? '已暂停（超出套餐）'
                  : readOnly
                    ? '只读（欠费）'
                    : '正常'}
            </dd>
          </div>
          <div>
            <dt>
              <IconPalette />
              主题
            </dt>
            <dd className="sitecard__theme">
              {site.themeName}
              <span className="sitecard__ver">@{site.themeVersion}</span>
            </dd>
          </div>
        </dl>

        {hint ? <p className="sitecard__hint">{hint}</p> : null}

        <div className="sitecard__actions">
          {archived || suspended ? (
            <Button variant="primary" disabled={busy || resumeBlocked} onClick={onResume}>
              {busy ? '恢复中…' : archived ? '恢复（重新占用名额）' : '恢复站点'}
            </Button>
          ) : (
            <Button
              disabled={!site.primaryHost}
              onClick={() => window.open(`https://${site.primaryHost}`, '_blank')}
            >
              打开站点
            </Button>
          )}
          <Button variant="ghost" onClick={onManageDomains}>
            管理域名
          </Button>
          {onArchive && !archived ? (
            <Button variant="ghost" disabled={busy} onClick={onArchive}>
              归档
            </Button>
          ) : null}
        </div>
      </div>
    </article>
  );
}

/* ── 图标 ──────────────────────────────────────────────────────────
 * 内联 SVG 而不是图标库：整页只用到这四个，多一个依赖不划算。
 * 统一 16 网格、1.3 描边、currentColor —— 跟着父级文字颜色走，
 * 不用为浅底深底各写一套。 */

function IconGlobe() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.3">
        <circle cx="8" cy="8" r="6" />
        <path d="M2 8h12" />
        <path d="M8 2c2.4 2.3 2.4 9.7 0 12M8 2c-2.4 2.3-2.4 9.7 0 12" strokeWidth="1.1" />
      </g>
    </svg>
  );
}

function IconChat() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round">
        <path d="M2.5 3.2h11v7.2H7l-3.3 2.6v-2.6H2.5z" />
        <path d="M5.4 6.8h5.2" strokeWidth="1.1" />
      </g>
    </svg>
  );
}

function IconPulse() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
        <path d="M1.5 8h3l1.8-4 2.6 8 1.8-4h3.8" />
      </g>
    </svg>
  );
}

function IconPalette() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.3">
        <path d="M8 1.8a6.2 6.2 0 0 0 0 12.4c1 0 1.4-.6 1.4-1.3 0-.8-.7-1-.7-1.7 0-.6.5-1 1.1-1h1.4a2.9 2.9 0 0 0 2.9-2.9A5.9 5.9 0 0 0 8 1.8Z" />
        <circle cx="5.6" cy="6" r="0.9" fill="currentColor" stroke="none" />
        <circle cx="8.9" cy="4.6" r="0.9" fill="currentColor" stroke="none" />
        <circle cx="4.6" cy="9.2" r="0.9" fill="currentColor" stroke="none" />
      </g>
    </svg>
  );
}
